import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Participant } from '@/stores/useCallStore'

interface VoiceTokenResponse {
  /** Twilio token, kept for clients that only read this field. */
  token: string
  identity: string
  ttl?: number
  /** One phone per carrier the company has numbers with. */
  telephones?: {
    fournisseur: 'twilio' | 'telnyx'
    token: string
    expire_dans: number
    /** Telnyx only: server-signed agent identity for outbound calls. */
    identite?: string
  }[]
}

const VOICE_TOKEN_KEY = ['voice-token']

/**
 * Récupère le token Twilio Voice. TanStack Query gère le cache + retry.
 * Le hook useTwilioDevice écoute 'tokenWillExpire' et invalide cette query.
 */
export function useVoiceToken(enabled = true) {
  return useQuery({
    queryKey: VOICE_TOKEN_KEY,
    queryFn: async () => {
      // ⚠️ platform='web' est OBLIGATOIRE : sans ça le backend défaut à 'ios' et
      // ajoute un push_credential_sid PushKit → les appels entrants sont routés via
      // APNs au lieu du browser et ne sonnent jamais en WebRTC.
      const { data } = await api.post<VoiceTokenResponse>('/api/voice/token/', {
        platform: 'web',
      })
      return data
    },
    enabled,
    staleTime: 50 * 60 * 1000, // 50 min (token expire en ~60 min)
    retry: 1,
  })
}

/** Force le refresh du token (appelé sur 'tokenWillExpire'). */
export function invalidateVoiceToken(queryClient: ReturnType<typeof useQueryClient>) {
  return queryClient.invalidateQueries({ queryKey: VOICE_TOKEN_KEY })
}

/**
 * Après l'achat ou la libération d'un numéro : relit l'opérateur de chaque numéro et le jeton
 * voix. Sans ça, le premier appel avec un numéro Telnyx neuf partait par Twilio, et le
 * téléphone Telnyx restait éteint tant que le jeton (50 min) n'en contenait pas.
 */
export function rafraichirTelephone(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['available-caller-ids'] })
  return invalidateVoiceToken(queryClient)
}

// ============================================================
// Hold / Unhold / Transfer mutations
// ============================================================

interface HoldResponse {
  success: boolean
  conference_name?: string
  on_hold: boolean
  /** 'none': the call is in an announcement conference, the agent's leg stays. */
  rejoin_mode?: 'conference' | 'none'
}

export function useHoldCall() {
  return useMutation({
    mutationFn: async (callSid: string) => {
      const { data } = await api.post<HoldResponse>(`/api/calls/${callSid}/hold/`)
      return data
    },
  })
}

export function useUnholdCall() {
  return useMutation({
    mutationFn: async (callSid: string) => {
      const { data } = await api.post<HoldResponse>(`/api/calls/${callSid}/unhold/`)
      return data
    },
  })
}

export type TransferTargetType = 'number' | 'web_agent' | 'phone_agent' | 'team'

interface TransferInput {
  callSid: string
  targetType: TransferTargetType
  target: string | number
  /** add: the person joins the call without an announcement. */
  mode: 'cold' | 'warm' | 'add'
  callerId?: string
}

interface TransferResponse {
  success: boolean
  status: string
  mode: string
  destination: string
  // Announced transfer only
  conference?: string
  label?: string
  /** 'callback': the server calls this phone back into the conference;
   *  'none': the call was already there, this leg stays. */
  rejoin_mode?: 'callback' | 'conference' | 'none'
}

export function useTransferCall() {
  return useMutation({
    mutationFn: async ({
      callSid,
      targetType,
      target,
      mode,
      callerId,
    }: TransferInput) => {
      const { data } = await api.post<TransferResponse>(
        `/api/calls/${callSid}/transfer/`,
        {
          target_type: targetType,
          target,
          mode,
          ...(callerId ? { caller_id: callerId } : {}),
        },
      )
      return data
    },
  })
}

/** Announced transfer: the agent leaves, the caller talks to the colleague. */
export function useHandOverAnnonce() {
  return useMutation({
    mutationFn: async (conference: string) => {
      await api.post(`/api/calls/annonce/${encodeURIComponent(conference)}/passer/`)
    },
  })
}

/** After the announcement: the caller, the agent and the colleague talk together. */
export function useReunirAnnonce() {
  return useMutation({
    mutationFn: async (conference: string) => {
      const { data } = await api.post<{ participants?: Participant[] }>(
        `/api/calls/annonce/${encodeURIComponent(conference)}/reunir/`,
      )
      return data
    },
  })
}

/** Call with several people: one added person leaves. */
export function useRetirerParticipant() {
  return useMutation({
    mutationFn: async ({ conference, jambe }: { conference: string; jambe: string }) => {
      const { data } = await api.post<{ participants?: Participant[] }>(
        `/api/calls/annonce/${encodeURIComponent(conference)}/retirer/`,
        { participant: jambe },
      )
      return data
    },
  })
}

/** Announced transfer: the colleague leaves, the caller is back with the agent. */
export function useCancelAnnonce() {
  return useMutation({
    mutationFn: async (conference: string) => {
      await api.post(`/api/calls/annonce/${encodeURIComponent(conference)}/annuler/`)
    },
  })
}

// ============================================================
// Transfer targets — agents (web/phone) + teams de la company
// ============================================================

export interface TransferAgent {
  type: 'web' | 'phone'
  id: number
  name: string
  email?: string
  phone_e164?: string
  avatar_url: string
  status: string
  job_title: string
}

export interface TransferTeam {
  id: number
  name: string
  color: string
  color_hex: string
  member_count: number
  available_count: number
}

interface TransferTargetsResponse {
  agents: TransferAgent[]
  teams: TransferTeam[]
}

const TRANSFER_TARGETS_KEY = ['transfer-targets']

/**
 * Liste des cibles possibles pour un transfert : agents web (UserProfile),
 * agents téléphone (PSTN), équipes (Team). Backend filtre déjà sur la company
 * et exclut l'utilisateur courant.
 *
 * Le softphone iOS utilise ce même endpoint pour ses onglets Collègue / Équipe.
 */
export function useTransferTargets(enabled = true) {
  return useQuery({
    queryKey: TRANSFER_TARGETS_KEY,
    queryFn: async () => {
      const { data } = await api.get<TransferTargetsResponse>(
        '/api/calls/transfer-targets/',
      )
      return data
    },
    enabled,
    staleTime: 60 * 1000,
  })
}

// ============================================================
// Power Dialer voice token (subaccount)
// ============================================================

export function usePowerDialerVoiceToken(enabled = false) {
  return useQuery({
    queryKey: ['pd-voice-token'],
    queryFn: async () => {
      const { data } = await api.post<VoiceTokenResponse>(
        '/api/power-dialer/voice-token/',
        { platform: 'web' },
      )
      return data
    },
    enabled,
    staleTime: 50 * 60 * 1000,
    retry: false,
  })
}
